import { createContext, useContext, useEffect, useState } from "react";

const dict = {
  en: {
    nav: {
      story: "Story", oils: "Oils", process: "Process", quality: "Quality",
      community: "Community", films: "Films", gallery: "Gallery", enquire: "Enquire",
    },
    hero: {
      overline: "Arniveda Farm Technologies LLP",
      line1: "Gaon se",
      line2: "Ghar tak.",
      tagline: "Pure wood & cold pressed virgin oils, crafted by the women of the Sira Shakti Community — from the goodness of nature to the purity of your home.",
      ctaOils: "Meet the oils",
      ctaStory: "Our story",
      stats: ["Virgin Oils", "Women Producers", "Wood & Cold Pressed", "Sira Shakti Community"],
    },
    marquee: [
      "100% Wood Pressed", "Chemical & Preservative Free", "Traditionally Extracted",
      "Unrefined & Natural", "Gaon se Ghar Tak", "Sira Shakti Community", "Cold Pressed to Retain Aroma",
    ],
    manifesto: {
      overline: "The Manifesto",
      title1: "Why Arniveda",
      title2: "exists.",
      chapters: [
        {
          title: "The Survival Gap",
          body: "Rural women and Self-Help Groups across India secure government funding and micro-loans to craft authentic, traditional products — yet most micro-enterprises quietly fail. The barrier is not skill or effort. It is the absence of professional branding, tech-enabled supply chains, and a direct link to high-paying urban markets. Without market access, producers remain trapped in low-wage labour cycles instead of building wealth-generating businesses.",
        },
        {
          title: "Gaon se Ghar Tak",
          body: "Arniveda is the bridge. We operate an omnichannel brand and supply-chain platform — but we do not hire rural women as wage labourers. We act as a direct market catalyst for independent SHGs and women-led micro-enterprises, providing professional branding, quality standardisation, and a streamlined tech-enabled supply chain that carries their authentic products straight to urban and online consumers.",
        },
        {
          title: "Wealth Creators",
          body: "Our model turns wage-earners into business owners. Producers retain ownership of their enterprises and capture true market margins through a profit-sharing model — a percentage of our profit flows back to the SHGs, and a portion of everything we earn is reinvested in the education, health, and livelihood of the women of the Sira Shakti Community.",
        },
      ],
      caption: "Inauguration day at Mehagaon, Gwalior",
    },
    products: {
      overline: "The Oils",
      title1: "Four bottles.",
      title2: "One promise.",
      side: "Scanned the QR on your bottle? This is the story behind it — every drop pressed by hand, by women who own their craft.",
      enquire: "Enquire about this oil",
      items: [
        {
          name: "Virgin Black Mustard Oil", press: "100% Cold Pressed", size: "1 Litre (910 g)",
          notes: "Dark & punchy — the bold heart of the Indian kitchen.", uses: "Cooking · Pickles · Massage",
        },
        {
          name: "Virgin Yellow Mustard Oil", press: "100% Wood Pressed", size: "1 Litre (910 g)",
          notes: "Mildly punchy — gentler on the palate, kinder for everyday cooking.", uses: "Everyday cooking · Tempering",
        },
        {
          name: "Virgin Coconut Oil", press: "100% Wood Pressed", size: "1 Litre",
          notes: "Nutty and sweet — slow-pressed in a traditional wooden ghani.", uses: "Cooking · Hair · Skin",
        },
        {
          name: "Virgin Almond Oil", press: "100% Wood Pressed · Sweet Almond", size: "100 ml",
          notes: "Light and nourishing — pressed for health, skin and hair.", uses: "Health · Skin · Hair",
        },
      ],
    },
    process: {
      overline: "The Process",
      title1: "Slow by",
      title2: "design.",
      intro: "Machines built for speed strip oil of its soul. Our wooden ghani turns slowly — the way it has for generations — so every bottle keeps its aroma, pungency and goodness.",
      steps: [
        { title: "Seed selection", body: "Mustard, coconut and almond sourced from trusted local farms — sorted by hand, batch by batch." },
        { title: "Sun cleaning", body: "Seeds are cleaned and naturally sun-dried, never roasted, never chemically treated." },
        { title: "Wooden ghani press", body: "Slow, bull-driven wood pressing and cold pressing at low RPM — no heat, no solvents, nutrients intact." },
        { title: "Natural settling", body: "Oil rests so sediment settles on its own. We filter gently, never refine." },
        { title: "Hand bottling", body: "Bottled and labelled by the women of Sira Shakti, sealed with the batch number of the day." },
      ],
    },
    quality: {
      overline: "Quality at the Farm",
      title1: "Tested where",
      title2: "it is pressed.",
      side: "Every batch passes a battery of physical and sensory tests right at the production facility — the way our producers have judged oil for generations.",
      tests: [
        { title: "Aroma & smell", body: "Every batch is nose-tested for the signature fresh, pungent aroma. Flat or rancid batches never leave the facility." },
        { title: "Density check", body: "Specific gravity is measured per batch on-site to confirm purity and the absence of lighter, cheaper oils." },
        { title: "Clarity & sediment", body: "Visual inspection after natural settling — a whisper of sediment is the mark of an unrefined oil." },
        { title: "Pungency taste", body: "Mustard oil must bite back. Our taste panel verifies the natural pungency of every pressing." },
        { title: "Colour inspection", body: "The natural golden-to-dark hue is checked against the seed variety and pressing of the day." },
        { title: "Cold test", body: "Chilling reveals adulteration — blended oils cloud and separate; pure single-seed oil stays true." },
      ],
    },
    community: {
      overline: "The Community",
      title1: "Sira Shakti",
      title2: "Community.",
      body: "Behind every bottle stands the Sira Shakti Aajivika Producer Group — a collective of 10 to 12 women who press black mustard, yellow mustard, coconut and almond oil on traditional wood and cold press machines. They are not employees. They are owners — and every purchase strengthens their enterprise.",
      facts: [
        { strong: "10–12 women producers", rest: " — pressing, testing and bottling every batch by hand." },
        { strong: "Mehagaon, Dabra (Pichhore)", rest: " — Gwalior, Madhya Pradesh 475110, India." },
        { strong: "FSSAI licensed producer", rest: " — Lic. No. 21426570001447." },
      ],
      quote: "“Every drop you buy empowers a Sira Shakti Community.”",
      momentsCaption: "Moments from the unit — winnowing, copra breaking, and weighing every bottle",
    },
    videos: {
      overline: "Films from the Farm",
      title1: "Watch the oil",
      title2: "being born.",
      captions: [
        "Cold Pressed Black Mustard Oil — made by the women of Sira Shakti",
        "Wood Pressed Coconut Oil — made by the women of Sira Shakti",
        "Wood Pressed Yellow Mustard Oil — made by the women of Sira Shakti",
      ],
    },
    impact: {
      overline: "A Part of the Change",
      line1: "A portion of every profit is",
      line2: "reinvested in the women",
      line3: "of Sira Shakti.",
      pillars: [
        { title: "Education", body: "Schooling and skill-building for the producers' children and the community." },
        { title: "Health", body: "Healthcare support for the women who press every bottle you buy." },
        { title: "Livelihood", body: "Profit-sharing that flows back to the SHGs — ownership, not wages." },
      ],
    },
    gallery: {
      overline: "Life at Arniveda",
      title1: "Fresh from",
      title2: "@arniveda.farm",
      follow: "Follow on Instagram",
      caption: "Latest posts & reels from our Instagram — updated automatically",
    },
    enquiry: {
      overline: "Bring Arniveda Home",
      title1: "Order, gift,",
      title2: "or partner.",
      body: "Tell us which oil you would like — for your kitchen, corporate gifting, or your retail shelves — and we will call you back.",
      whatsapp: "WhatsApp us",
      email: "Email us",
      name: "Your name", namePh: "Full name",
      phone: "Phone / WhatsApp", phonePh: "+91 ...",
      product: "Which oil?",
      options: [
        "Virgin Black Mustard Oil — 1 L", "Virgin Yellow Mustard Oil — 1 L",
        "Virgin Coconut Oil — 1 L", "Virgin Almond Oil — 100 ml", "Bulk / B2B Enquiry",
      ],
      message: "Message (optional)", messagePh: "Quantity, city, gifting or B2B details...",
      submit: "Send enquiry", sending: "Sending...",
      toastSuccess: "Thank you! The Arniveda team will reach out to you shortly.",
      toastError: "Something went wrong. Please try WhatsApp or email instead.",
      toastValidation: "Please share your name and phone number.",
    },
    footer: {
      producedBy: "Produced by",
      marketedBy: "Marketed by",
      talkToUs: "Talk to us",
      follow: "Follow the farm",
      rights: "Arniveda Farm Technologies LLP. All rights reserved.",
      closing: "From the goodness of nature to the purity of your home.",
    },
    extras: {
      photoStripCaption: "Real moments from the Sira Shakti unit — no stock photos, only our people",
      filmsCaption: "Behind the films — the bottling station and the full processing unit",
      impactCaption: "Training, exposure visits and celebrations — the community in action",
      followFb: "Follow on Facebook",
      fbCaption: "Our Facebook page — latest updates from Arniveda, updated automatically",
    },
  },
  hi: {
    nav: {
      story: "कहानी", oils: "तेल", process: "प्रक्रिया", quality: "गुणवत्ता",
      community: "समुदाय", films: "फ़िल्में", gallery: "गैलरी", enquire: "पूछताछ",
    },
    hero: {
      overline: "अर्णिवेदा फ़ार्म टेक्नोलॉजीज़ LLP",
      line1: "गाँव से",
      line2: "घर तक.",
      tagline: "सीरा शक्ति समुदाय की महिलाओं द्वारा बनाए गए शुद्ध लकड़ी-घानी व कोल्ड प्रेस्ड वर्जिन तेल — प्रकृति की उदारता से आपके घर की शुद्धता तक।",
      ctaOils: "हमारे तेल देखें",
      ctaStory: "हमारी कहानी",
      stats: ["वर्जिन तेल", "महिला उत्पादक", "लकड़ी व कोल्ड प्रेस्ड", "सीरा शक्ति समुदाय"],
    },
    marquee: [
      "100% लकड़ी घानी प्रेस्ड", "रसायन व प्रिज़र्वेटिव मुक्त", "पारंपरिक विधि से निष्कर्षित",
      "बिना रिफ़ाइन, पूर्णतः प्राकृतिक", "गाँव से घर तक", "सीरा शक्ति समुदाय", "सुगंध सहित कोल्ड प्रेस्ड",
    ],
    manifesto: {
      overline: "हमारा संकल्प",
      title1: "अर्णिवेदा क्यों",
      title2: "ज़रूरी है.",
      chapters: [
        {
          title: "उत्तरजीविता की खाई (Survival Gap)",
          body: "भारत भर की ग्रामीण महिलाएँ और स्वयं सहायता समूह पारंपरिक उत्पाद बनाने के लिए सरकारी फ़ंडिंग और माइक्रो-लोन लेती हैं — फिर भी अधिकांश लघु उद्यम चुपचाप बंद हो जाते हैं। बाधा हुनर या मेहनत नहीं है, बल्कि पेशेवर ब्रांडिंग, तकनीकी सप्लाई चेन और शहरी बाज़ारों तक सीधी पहुँच का अभाव है। बाज़ार न मिलने पर उत्पादक कम मज़दूरी के चक्कर में फँसी रह जाती हैं, धन पैदा करने वाला व्यवसाय बनाने के बजाय।",
        },
        {
          title: "गाँव से घर तक",
          body: "अर्णिवेदा वह सेतु है। हम एक ओमनीचैनल ब्रांड और सप्लाई-चेन मंच चलाते हैं — पर हम ग्रामीण महिलाओं को मज़दूर के रूप में काम पर नहीं रखते। हम स्वतंत्र SHG और महिला-नेतृत्व वाले लघु उद्यमों के लिए सीधे बाज़ार-सूत्रधार का काम करते हैं — पेशेवर ब्रांडिंग, गुणवत्ता मानकीकरण और सुव्यवस्थित तकनीकी सप्लाई चेन देकर, जो उनके असली उत्पादों को सीधे शहरी और ऑनलाइन ग्राहकों तक पहुँचाती है।",
        },
        {
          title: "धन-सृजक महिलाएँ",
          body: "हमारा मॉडल मज़दूर को उद्यमी बनाता है। उत्पादक अपने उद्यम की मालिक बनी रहती हैं और लाभ-साझेदारी मॉडल से असली बाज़ार मार्जिन पाती हैं — हमारे लाभ का एक प्रतिशत SHG को वापस मिलता है, और हमारी कमाई का एक हिस्सा सीरा शक्ति समुदाय की महिलाओं की शिक्षा, स्वास्थ्य और आजीविका में पुनः निवेशित होता है।",
        },
      ],
      caption: "मेहगाँव, ग्वालियर में उद्घाटन का दिन",
    },
    products: {
      overline: "हमारे तेल",
      title1: "चार बोतलें.",
      title2: "एक वादा.",
      side: "क्या आपने अपनी बोतल पर QR कोड स्कैन किया? यही उसके पीछे की कहानी है — हर बूंद उन महिलाओं के हाथों से निकली है जो अपने शिल्प की मालिक हैं।",
      enquire: "इस तेल हेतु पूछताछ करें",
      items: [
        {
          name: "वर्जिन काली सरसों का तेल", press: "100% कोल्ड प्रेस्ड", size: "1 लीटर (910 ग्रा)",
          notes: "गहरा और तीखा — भारतीय रसोई का निडर हृदय।", uses: "खाना · अचार · मालिश",
        },
        {
          name: "वर्जिन पीली सरसों का तेल", press: "100% वुड प्रेस्ड", size: "1 लीटर (910 ग्रा)",
          notes: "हल्का तीखापन — तालु पर नरम, रोज़ाना के खाने के लिए अनुकूल।", uses: "रोज़ाना खाना · तड़का",
        },
        {
          name: "वर्जिन नारियल तेल", press: "100% वुड प्रेस्ड", size: "1 लीटर",
          notes: "नटी और मीठा — पारंपरिक लकड़ी की घानी में धीमा निष्कर्षण।", uses: "खाना · बाल · त्वचा",
        },
        {
          name: "वर्जिन बादाम तेल", press: "100% वुड प्रेस्ड · स्वीट बादाम", size: "100 मि.ली.",
          notes: "हल्का और पौष्टिक — स्वास्थ्य, त्वचा और बालों के लिए निष्कर्षित।", uses: "स्वास्थ्य · त्वचा · बाल",
        },
      ],
    },
    process: {
      overline: "प्रक्रिया",
      title1: "जानबूझकर",
      title2: "धीमी.",
      intro: "रफ़्तार के लिए बनी मशीनें तेल की आत्मा छीन लेती हैं। हमारी लकड़ी की घानी धीरे घूमती है — जैसे पीढ़ियों से होता आया है — ताकि हर बोतल में सुगंध, तीखापन और गुण बने रहें।",
      steps: [
        { title: "बीजों का चुनाव", body: "सरसों, नारियल और बादाम भरोसेमंद स्थानीय खेतों से — हर बैच हाथ से छाँटा जाता है।" },
        { title: "धूप में सफ़ाई", body: "बीज साफ़ कर प्राकृतिक धूप में सुखाए जाते हैं — न भुने जाते हैं, न रासायनिक उपचार होता है।" },
        { title: "लकड़ी की घानी", body: "बैल-चालित लकड़ी की घानी और कोल्ड प्रेस से धीमी गति पर निष्कर्षण — न गर्मी, न सॉल्वेंट; पोषक तत्व सुरक्षित।" },
        { title: "प्राकृतिक निस्तारण", body: "तेल को विश्राम दिया जाता है ताकि अवक्षेप अपने आप बैठ जाए। हम हल्का छानते हैं, कभी रिफ़ाइन नहीं करते।" },
        { title: "हाथ से बॉटलिंग", body: "सीरा शक्ति की महिलाओं द्वारा बॉटलिंग और लेबलिंग — उसी दिन के बैच नंबर के साथ सील।" },
      ],
    },
    quality: {
      overline: "फ़ार्म पर गुणवत्ता",
      title1: "परख वहीं होती है",
      title2: "जहाँ तेल निकलता है.",
      side: "हर बैच उत्पादन स्थल पर ही भौतिक और संवेदी परीक्षणों से गुज़रता है — ठीक वैसे, जैसे हमारी उत्पादक महिलाएँ पीढ़ियों से तेल परखती आई हैं।",
      tests: [
        { title: "सुगंध परीक्षण", body: "हर बैच की ताज़ा, तीखी सुगंध जाँची जाती है। बेजान या बासी बैच सुविधा से बाहर नहीं जाते।" },
        { title: "घनत्व जाँच", body: "शुद्धता सुनिश्चित करने और हल्के-सस्ते तेलों की मिलावट पकड़ने हेतु हर बैच का घनत्व मौके पर मापा जाता है।" },
        { title: "स्वच्छता व अवक्षेप", body: "प्राकृतिक निस्तारण के बाद दृश्य निरीक्षण — हल्का अवक्षेप बिना रिफ़ाइन किए तेल की पहचान है।" },
        { title: "तीखापन (स्वाद)", body: "सरसों के तेल में तीखापन होना ही चाहिए। हमारा स्वाद-पैनल हर निष्कर्षण का प्राकृतिक तीखापन सत्यापित करता है।" },
        { title: "रंग निरीक्षण", body: "बीज की किस्म और उस दिन के निष्कर्षण के अनुरूप प्राकृतिक सुनहरे से गहरे रंग की जाँच होती है।" },
        { title: "शीत परीक्षण", body: "ठंडा करने पर मिलावट प्रकट हो जाती है — मिले हुए तेल धुंधले होकर अलग हो जाते हैं; शुद्ध एकल-बीज तेल सच्चा रहता है।" },
      ],
    },
    community: {
      overline: "समुदाय",
      title1: "सीरा शक्ति",
      title2: "समुदाय.",
      body: "हर बोतल के पीछे सीरा शक्ति आजीविका उत्पादक समूह खड़ा है — 10 से 12 महिलाओं का समूह, जो पारंपरिक लकड़ी व कोल्ड प्रेस मशीनों पर काली सरसों, पीली सरसों, नारियल और बादाम का तेल निकालती हैं। वे कर्मचारी नहीं हैं — वे मालिक हैं, और आपकी हर खरीद उनके उद्यम को मज़बूत करती है।",
      facts: [
        { strong: "10–12 महिला उत्पादक", rest: " — हर बैच को हाथ से निकालना, परखना और बोतल भरना।" },
        { strong: "मेहगाँव, डबरा (पिछोर)", rest: " — ग्वालियर, मध्य प्रदेश 475110, भारत।" },
        { strong: "FSSAI लाइसेंस प्राप्त उत्पादक", rest: " — लाइसेंस सं. 21426570001447।" },
      ],
      quote: "“आपकी खरीदी गई हर बूंद एक सीरा शक्ति समुदाय को सशक्त बनाती है।”",
      momentsCaption: "इकाई के क्षण — बीजों की सफ़ाई, कोपरा तोड़ना, और हर बोतल का वज़न",
    },
    videos: {
      overline: "फ़ार्म की फ़िल्में",
      title1: "तेल को बनते",
      title2: "देखिए.",
      captions: [
        "कोल्ड प्रेस्ड काली सरसों का तेल — सीरा शक्ति की महिलाओं द्वारा निर्मित",
        "वुड प्रेस्ड नारियल तेल — सीरा शक्ति की महिलाओं द्वारा निर्मित",
        "वुड प्रेस्ड पीली सरसों का तेल — सीरा शक्ति की महिलाओं द्वारा निर्मित",
      ],
    },
    impact: {
      overline: "बदलाव का हिस्सा",
      line1: "हर मुनाफ़े का एक हिस्सा",
      line2: "सीरा शक्ति की महिलाओं में",
      line3: "लौट जाता है.",
      pillars: [
        { title: "शिक्षा", body: "उत्पादकों के बच्चों और समुदाय के लिए पढ़ाई और कौशल-विकास।" },
        { title: "स्वास्थ्य", body: "उन महिलाओं के लिए स्वास्थ्य सहायता, जो आपकी हर बोतल निकालती हैं।" },
        { title: "आजीविका", body: "लाभ-साझेदारी जो SHG तक वापस पहुँचती है — वेतन नहीं, स्वामित्व।" },
      ],
    },
    gallery: {
      overline: "अर्णिवेदा की ज़िंदगी",
      title1: "ताज़ा पोस्ट —",
      title2: "@arniveda.farm",
      follow: "इंस्टाग्राम पर फ़ॉलो करें",
      caption: "हमारे इंस्टाग्राम की नवीनतम पोस्ट व रील्स — स्वतः अपडेट होती हैं",
    },
    enquiry: {
      overline: "अर्णिवेदा को घर लाइए",
      title1: "ऑर्डर, उपहार,",
      title2: "या साझेदारी.",
      body: "बताइए आपको कौन-सा तेल चाहिए — आपकी रसोई, कॉर्पोरेट गिफ़्टिंग या आपकी रिटेल दुकान के लिए — हम आपको कॉल करेंगे।",
      whatsapp: "व्हाट्सऐप करें",
      email: "ईमेल करें",
      name: "आपका नाम", namePh: "पूरा नाम",
      phone: "फ़ोन / व्हाट्सऐप", phonePh: "+91 ...",
      product: "कौन-सा तेल?",
      options: [
        "वर्जिन काली सरसों का तेल — 1 ली", "वर्जिन पीली सरसों का तेल — 1 ली",
        "वर्जिन नारियल तेल — 1 ली", "वर्जिन बादाम तेल — 100 मि.ली.", "थोक / B2B पूछताछ",
      ],
      message: "संदेश (वैकल्पिक)", messagePh: "मात्रा, शहर, गिफ़्टिंग या B2B विवरण...",
      submit: "पूछताछ भेजें", sending: "भेजा जा रहा है...",
      toastSuccess: "धन्यवाद! अर्णिवेदा टीम शीघ्र ही आपसे संपर्क करेगी।",
      toastError: "कुछ गड़बड़ हुई। कृपया व्हाट्सऐप या ईमेल से संपर्क करें।",
      toastValidation: "कृपया अपना नाम और फ़ोन नंबर बताएं।",
    },
    footer: {
      producedBy: "उत्पादक",
      marketedBy: "विपणनकर्ता",
      talkToUs: "बात करें",
      follow: "फ़ार्म को फ़ॉलो करें",
      rights: "अर्णिवेदा फ़ार्म टेक्नोलॉजीज़ LLP. सर्वाधिकार सुरक्षित.",
      closing: "प्रकृति की उदारता से आपके घर की शुद्धता तक।",
    },
    extras: {
      photoStripCaption: "सीरा शक्ति इकाई के असली क्षण — हमारी अपनी टीम, हमारा अपना काम",
      filmsCaption: "फ़िल्मों के पीछे — बॉटलिंग स्टेशन और पूरी प्रसंस्करण इकाई",
      impactCaption: "प्रशिक्षण, एक्सपोज़र विज़िट और उत्सव — सक्रिय समुदाय",
      followFb: "फ़ेसबुक पर फ़ॉलो करें",
      fbCaption: "हमारा फ़ेसबुक पेज — अर्णिवेदा की नवीनतम अपडेट्स, स्वतः अपडेट होती हैं",
    },
  },
};

const LangContext = createContext(null);

export const LangProvider = ({ children }) => {
  const [lang, setLang] = useState(() => localStorage.getItem("arniveda-lang") || "en");
  useEffect(() => {
    localStorage.setItem("arniveda-lang", lang);
  }, [lang]);
  const t = (path) => {
    const val = path.split(".").reduce((o, k) => (o == null ? o : o[k]), dict[lang]);
    return val == null ? path : val;
  };
  return <LangContext.Provider value={{ lang, setLang, t }}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
